import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, X, Loader2, Users, ClipboardList, Send, CalendarCheck, KeyRound, Trash2, ListChecks } from "lucide-react";
import ClientJobBreakdown from "@/components/ClientJobBreakdown";
import Modal from "@/components/Modal";

const inputCls =
  "mt-1.5 w-full bg-[#0D0E15] border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500/60 transition-colors";

export default function Executives() {
  const [execs, setExecs] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [detailExec, setDetailExec] = useState(null);
  const [detail, setDetail] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = () => api.get("/admin/executives").then((r) => setExecs(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = async (e) => {
    setDetailExec(e);
    setLoadingDetail(true);
    setDetail([]);
    try {
      const { data } = await api.get(`/executives/${e.id}/breakdown`);
      setDetail(data.clients || []);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoadingDetail(false); }
  };

  const deleteExec = async (e) => {
    if (!window.confirm(`Delete executive ${e.name}? They will be unassigned from all their clients.`)) return;
    try { await api.delete(`/admin/executives/${e.id}`); toast.success("Executive deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const changePassword = async (e) => {
    const pw = window.prompt(`Set a new password for ${e.name}:`);
    if (!pw) return;
    try { await api.post(`/admin/executives/${e.id}/password`, { password: pw }); toast.success("Password updated"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("All fields are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/executives", form);
      toast.success("Executive account created");
      setCreated(data);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-100 tracking-tight">Executives</h1>
          <p className="text-slate-400 text-sm mt-1">Create executive accounts and see how many jobs each has analysed.</p>
        </div>
        <button data-testid="new-executive-btn" onClick={() => { setCreated(null); setOpen(true); }} className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
          <UserPlus className="w-4 h-4" /> New executive
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {execs.map((e) => (
          <div key={e.id} data-testid={`exec-card-${e.id}`} className="bg-[#12151F] border border-slate-800/80 rounded-xl p-5 shadow-lg shadow-black/40">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-300 font-bold">{e.name?.[0] || "?"}</div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-100 truncate">{e.name}</div>
                <div className="text-xs text-slate-500 truncate">{e.email}</div>
              </div>
              <button data-testid={`exec-password-${e.id}`} onClick={() => changePassword(e)} title="Change password" className="text-slate-500 hover:text-indigo-300 transition-colors p-1">
                <KeyRound className="w-4 h-4" />
              </button>
              <button data-testid={`exec-delete-${e.id}`} onClick={() => deleteExec(e)} title="Delete executive" className="text-slate-500 hover:text-rose-400 transition-colors p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <Stat icon={Users} label="Clients" value={e.clients_count} color="text-blue-400" testid={`exec-clients-${e.id}`} />
              <Stat icon={ClipboardList} label="Analysed" value={e.analysed_count} color="text-indigo-400" testid={`exec-analysed-${e.id}`} />
              <Stat icon={Send} label="Applied" value={e.applied_count} color="text-purple-400" testid={`exec-applied-${e.id}`} />
              <Stat icon={CalendarCheck} label="Interviews" value={e.interviews_count} color="text-amber-400" testid={`exec-interviews-${e.id}`} />
            </div>
            <button
              data-testid={`exec-view-jobs-${e.id}`}
              onClick={() => openDetail(e)}
              className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 rounded-lg py-2 transition-colors"
            >
              <ListChecks className="w-4 h-4" /> View clients &amp; jobs
            </button>
          </div>
        ))}
        {execs.length === 0 && <div className="col-span-full text-center text-slate-500 py-16 text-sm">No executives yet.</div>}
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)}>
          <div className="bg-[#141724] border border-slate-800 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/60">
              <h3 className="font-heading font-semibold text-slate-100">New executive account</h3>
              <button data-testid="close-exec-modal" onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {created ? (
                <div className="text-sm text-slate-300 space-y-2">
                  <div className="flex items-center gap-2 text-emerald-400"><KeyRound className="w-4 h-4" /> Account created</div>
                  <div className="bg-[#0D0E15] border border-slate-800 rounded-lg p-3 font-mono text-xs">
                    <div>Name: {created.name}</div>
                    <div>Email: {created.email}</div>
                  </div>
                  <p className="text-xs text-slate-500">Share the email and the password you set with the executive. They can now sign in and be assigned clients.</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Full name</label>
                    <input data-testid="exec-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex Rivera" className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</label>
                    <input data-testid="exec-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="alex@applypilot.com" className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Password</label>
                    <input data-testid="exec-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Set a password" className={inputCls} />
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-800/60">
              <button onClick={() => setOpen(false)} className="text-sm text-slate-400 hover:text-slate-200 px-4 py-2">{created ? "Close" : "Cancel"}</button>
              {!created && (
                <button data-testid="save-executive" onClick={create} disabled={saving} className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create executive"}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {detailExec && (
        <Modal onClose={() => setDetailExec(null)}>
          <div className="bg-[#0D0E15] border border-slate-800 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/60">
              <div>
                <h3 className="font-heading font-semibold text-slate-100">{detailExec.name} — clients &amp; jobs</h3>
                <p className="text-xs text-slate-500 mt-0.5">Every client this executive handles and the jobs analysed for each.</p>
              </div>
              <button data-testid="close-detail-modal" onClick={() => setDetailExec(null)} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              <ClientJobBreakdown clients={detail} loading={loadingDetail} />
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="bg-[#0D0E15] border border-slate-800/60 rounded-lg px-3 py-2">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
      <Icon className={`w-3.5 h-3.5 ${color}`} /> {label}
    </div>
    <div className={`font-mono font-bold text-xl mt-0.5 ${color}`}>{value}</div>
  </div>
);
